import React, { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import {
  Clock,
  DollarSign,
  TrendingUp,
  Share2,
  Copy,
  Presentation,
  Users,
  Wrench,
} from "lucide-react";
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const AITimeROIWidget = () => {
  const [weeklyTimeInvested, setWeeklyTimeInvested] = useState(20); // minutes
  const [timeSavedPerTask, setTimeSavedPerTask] = useState(20); // minutes
  const [valueInputType, setValueInputType] = useState("hourly"); // 'hourly' or 'salary'
  const [hourlyRate, setHourlyRate] = useState(50);
  const [annualSalary, setAnnualSalary] = useState(100000);

  const effectiveHourlyRate =
    valueInputType === "hourly" ? hourlyRate : annualSalary / 2000;

  const chartData = useMemo(() => {
    const data = [];
    let cumulativeTimeSaved = 0;
    let cumulativeTimeInvested = 0;
    let automatedTasks = 0;
    const totalInvestmentNeededForOneTask = 60; // It always takes 1 hour to automate a task
    let investmentProgress = 0;

    for (let week = 0; week <= 52; week++) {
      const weeklySavings = automatedTasks * timeSavedPerTask;
      cumulativeTimeSaved += weeklySavings;

      if (week > 0) {
        cumulativeTimeInvested += weeklyTimeInvested;
        investmentProgress += weeklyTimeInvested;

        if (
          totalInvestmentNeededForOneTask > 0 &&
          investmentProgress >= totalInvestmentNeededForOneTask
        ) {
          const newTasksAutomated = Math.floor(
            investmentProgress / totalInvestmentNeededForOneTask,
          );
          automatedTasks += newTasksAutomated;
          investmentProgress %= totalInvestmentNeededForOneTask;
        }
      }

      const dollarValue = (cumulativeTimeSaved / 60) * effectiveHourlyRate;

      data.push({
        week,
        cumulativeTimeSaved: Math.round(cumulativeTimeSaved),
        cumulativeTimeInvested: Math.round(cumulativeTimeInvested),
        dollarValue: Math.round(dollarValue),
        netTimeSaved: Math.round(cumulativeTimeSaved - cumulativeTimeInvested),
      });
    }

    return data;
  }, [weeklyTimeInvested, timeSavedPerTask, effectiveHourlyRate]);

  const finalStats = chartData[chartData.length - 1];
  const totalROT =
    finalStats.cumulativeTimeInvested > 0
      ? finalStats.cumulativeTimeSaved / finalStats.cumulativeTimeInvested
      : 0;

  const formatTime = (minutes: number) => {
    if (minutes < 0) {
      const positiveMinutes = Math.abs(minutes);
      const hours = Math.floor(positiveMinutes / 60);
      const mins = positiveMinutes % 60;
      let timeString = "";
      if (hours > 0) timeString += `${hours}h `;
      if (mins > 0) timeString += `${mins}m`;
      return `-${timeString.trim()}`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const copyResults = () => {
    const text = `🤖 My AI Time ROT: Invested ${formatTime(finalStats.cumulativeTimeInvested)}, saved ${formatTime(finalStats.cumulativeTimeSaved)} = ${totalROT.toFixed(1)}x return! Worth $${finalStats.dollarValue.toLocaleString()} 💰`;
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl shadow-lg">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          🚀 What's 20 Minutes a Week Worth to You?
        </h1>
        <p className="text-gray-800 max-w-4xl mx-auto">
          This calculator shows that even when it takes{" "}
          <span className="font-bold text-gray-800">1 hour</span> to automate a
          task, the long-term payoff from small, consistent weekly efforts is
          huge.
        </p>
      </div>

      {/* Input Controls */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {/* Weekly Time Invested */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center mb-4">
            <Clock className="w-5 h-5 text-blue-500 mr-2" />
            <h3 className="font-semibold text-gray-800">
              Weekly Time Invested
            </h3>
          </div>
          <div className="space-y-4">
            <input
              type="range"
              min="5"
              max="90"
              step="5"
              value={weeklyTimeInvested}
              onChange={(e) => setWeeklyTimeInvested(Number(e.target.value))}
              className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="text-center">
              <span className="text-2xl font-bold text-blue-600">
                {formatTime(weeklyTimeInvested)}
              </span>
              <p className="text-sm text-gray-500">
                per week learning/building
              </p>
            </div>
          </div>
        </div>

        {/* Time Saved Per Task */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center mb-4">
            <TrendingUp className="w-5 h-5 text-green-500 mr-2" />
            <h3 className="font-semibold text-gray-800">
              Time Saved Per Automation
            </h3>
          </div>
          <div className="space-y-4">
            <input
              type="range"
              min="10"
              max="180"
              step="10"
              value={timeSavedPerTask}
              onChange={(e) => setTimeSavedPerTask(Number(e.target.value))}
              className="w-full h-2 bg-green-200 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="text-center">
              <span className="text-2xl font-bold text-green-600">
                {formatTime(timeSavedPerTask)}
              </span>
              <p className="text-sm text-gray-500">per week once automated</p>
            </div>
          </div>
        </div>

        {/* Value of Time */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center mb-4">
            <DollarSign className="w-5 h-5 text-purple-500 mr-2" />
            <h3 className="font-semibold text-gray-800">Value of Your Time</h3>
          </div>
          <div className="space-y-4">
            {valueInputType === "hourly" ? (
              <div>
                <input
                  type="range"
                  min="20"
                  max="500"
                  step="10"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(Number(e.target.value))}
                  className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="text-center">
                  <span className="text-2xl font-bold text-purple-600">
                    ${hourlyRate}
                  </span>
                  <p className="text-sm text-gray-500">per hour</p>
                </div>
              </div>
            ) : (
              <div>
                <input
                  type="range"
                  min="40000"
                  max="300000"
                  step="20000"
                  value={annualSalary}
                  onChange={(e) => setAnnualSalary(Number(e.target.value))}
                  className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="text-center">
                  <span className="text-2xl font-bold text-purple-600">
                    ${annualSalary.toLocaleString()}
                  </span>
                  <p className="text-sm text-gray-500">
                    per year (≈${Math.round(effectiveHourlyRate)}/hour)
                  </p>
                </div>
              </div>
            )}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setValueInputType("hourly")}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  valueInputType === "hourly"
                    ? "bg-white text-purple-600 shadow-sm"
                    : "text-gray-600 hover:text-purple-600"
                }`}
              >
                Hourly Rate
              </button>
              <button
                onClick={() => setValueInputType("salary")}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  valueInputType === "salary"
                    ? "bg-white text-purple-600 shadow-sm"
                    : "text-gray-600 hover:text-purple-600"
                }`}
              >
                Annual Salary
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Time Compounding Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">
            Time Compounding Over 52 Weeks
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis tickFormatter={(value) => `${Math.round(value / 60)}h`} />
              <Tooltip
                formatter={(value: number, name: string) => [
                  formatTime(value),
                  name,
                ]}
                labelFormatter={(label: string) => `Week ${label}`}
              />
              <Line
                type="monotone"
                dataKey="cumulativeTimeSaved"
                stroke="#10b981"
                strokeWidth={3}
                name="Time Saved"
              />
              <Line
                type="monotone"
                dataKey="cumulativeTimeInvested"
                stroke="#6366f1"
                strokeWidth={3}
                name="Time Invested"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* ROI Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">
            Dollar Value Created
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis
                tickFormatter={(value) => `$${Math.round(value / 1000)}k`}
              />
              <Tooltip
                formatter={(value: number) => [
                  `$${value.toLocaleString()}`,
                  "Value Created",
                ]}
                labelFormatter={(label: string) => `Week ${label}`}
              />
              <Bar dataKey="dollarValue" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="text-center px-6 mb-8">
        <p className="text-gray-600 max-w-2xl mx-auto">
          The "Time Saved" curve grows exponentially as each new automation adds
          to your recurring weekly savings. This model makes one assumption:{" "}
          <span className="font-bold text-gray-800">
            that it takes 1 hour of focused work to automate one task.
          </span>{" "}
          Your weekly time investment determines how fast you get it done.
        </p>
      </div>

      {/* Results Summary */}
      <div className="bg-gradient-to-r from-blue-400 to-indigo-600 rounded-xl p-6 text-white text-center">
        <h2 className="text-2xl font-bold mb-4">Your Results 🎉</h2>
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white/20 rounded-lg p-4">
            <div className="text-3xl font-bold">
              {formatTime(finalStats.cumulativeTimeInvested)}
            </div>
            <div className="text-sm opacity-90">Time Invested</div>
          </div>
          <div className="bg-white/20 rounded-lg p-4">
            <div className="text-3xl font-bold">
              {formatTime(finalStats.cumulativeTimeSaved)}
            </div>
            <div className="text-sm opacity-90">Time Saved</div>
          </div>
          <div className="bg-white/20 rounded-lg p-4">
            <div className="text-3xl font-bold">{totalROT.toFixed(1)}x</div>
            <div className="text-sm opacity-90">Return on Time</div>
          </div>
        </div>
        <div className="mb-6">
          <div className="text-4xl font-bold mb-2">
            ${finalStats.dollarValue.toLocaleString()}
          </div>
          <div className="text-lg opacity-90">
            Total Value Created in 52 weeks
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() =>
              window.open(
                "https://x.com/intent/tweet?text=" +
                  encodeURIComponent(
                    `🤖 Just calculated my AI Time ROT: ${totalROT.toFixed(1)}x return worth $${finalStats.dollarValue.toLocaleString()}! Tiny steps, big payoff 💰 Try it yourself: https://8020agent.com/20`,
                  ),
              )
            }
            className="flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share on X
          </button>
        </div>
      </div>

      {/* NEW "From Ads to AI" CTA Card */}
      <div className="mt-12 bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Ready to Go From Ads to AI?
          </h2>
          <p className="text-xl text-gray-600 mb-6 max-w-2xl mx-auto">
            This calculator shows the power of small steps. <br />
            My community helps you take them.
          </p>
          <div className="grid md:grid-cols-3 gap-6 text-left mb-8 max-w-4xl mx-auto">
            <div className="flex items-start space-x-3">
              <div className="bg-purple-100 rounded-full p-2">
                <Presentation className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">
                  Monthly Masterclasses
                </h4>
                <p className="text-sm text-gray-600">
                  Live sessions on the latest AI tools and strategies for Google
                  Ads.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-purple-100 rounded-full p-2">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">
                  Private Community
                </h4>
                <p className="text-sm text-gray-600">
                  Connect with fellow forward-thinking marketers and get your
                  questions answered.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-purple-100 rounded-full p-2">
                <Wrench className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">
                  Scripts & Snippets
                </h4>
                <p className="text-sm text-gray-600">
                  Get access to ready-made tools to get better results from your
                  campaigns.
                </p>
              </div>
            </div>
          </div>
          <a
            href="/ai"
            className="inline-block bg-primary text-primary-foreground rounded-lg font-semibold px-8 py-4 text-lg hover:bg-primary/90 transition-colors"
          >
            See Everything You'll Get
          </a>
        </div>
      </div>
    </div>
  );
};

export default AITimeROIWidget;
